import DS from 'ember-data';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { computed } from '@ember/object';
import { equal, sort, not, alias, and } from '@ember/object/computed';
import { belongsTo, hasMany } from 'ember-data/relationships';
import { inject as service } from '@ember/service';
import { modelApiAction } from 'cio/utils/model-api-actions';
import joinWords from 'cio/utils/join-words';
import { sanitize } from 'cio/utils/sanitize';
import { config } from 'cio/helpers/preview-whitelist';
import TemplateModel from 'cio/models/template';
import TagModel from 'cio/models/tag';
import ApiService from 'cio/services/api';
import { Platform } from 'cio/models/newsletter';
import { isEmpty } from '@ember/utils';

export enum ContentSource {
  Default = '',
  Api = 'api',
  Template = 'template',
}

export enum TransactionalMessageChannelType {
  Email = 'email',
  Push = 'push',
}

export interface TransactionalMessageChannelInfo {
  name: string;
  icon: string;
  settingsPath: string;
  enabled?: boolean;
  tooltipContent?: string;
  beta?: boolean;
}

export interface TransactionalMessageChannel {
  type: TransactionalMessageChannelType;
  info: TransactionalMessageChannelInfo;
}

export const TransactionalMessageChannels: TransactionalMessageChannel[] = [
  {
    type: TransactionalMessageChannelType.Email,
    info: {
      name: 'Email',
      icon: 'email',
      settingsPath: 'actions.email',
    },
  },
  {
    type: TransactionalMessageChannelType.Push,
    info: {
      name: 'Push',
      icon: 'push',
      settingsPath: 'actions.push',
    },
  },
];

export enum TransactionalMessageUpdateType {
  Settings = 'settings',
  Activate = 'activate',
  ContentSource = 'content_source',
  NameAndDescription = 'name_and_description',
  Channel = 'channel',
  SampleData = 'sample_data',
}

export enum TransactionalMessageState {
  Active = 'active',
  Draft = 'draft',
}

export const SettingsKeys = ['sendToUnsubscribed', 'linkTracking', 'hideMessageBody', 'queueDrafts'] as const;
export type SettingsKey = (typeof SettingsKeys)[number];

export default class TransactionalMessageModel extends Model {
  @service api!: ApiService;
  @service store!: DS.Store;

  @attr('string') name!: string;
  @attr('string', { defaultValue: TransactionalMessageChannelType.Email }) type!: TransactionalMessageChannelType;
  @attr('string') description!: string;
  @attr('string') contentSource!: ContentSource;
  @attr('string') updateType!: TransactionalMessageUpdateType;
  @attr('string') state!: TransactionalMessageState;
  @attr('boolean') isDefault!: boolean;
  @attr('string') sampleData!: string;

  @attr('boolean') sendToUnsubscribed!: boolean;
  @attr('boolean') linkTracking!: boolean;
  @attr('boolean') hideMessageBody!: boolean;
  @attr('boolean') queueDrafts!: boolean;
  @attr('string') triggerName!: string;

  @belongsTo('template', { async: false, inverse: null }) template!: TemplateModel;
  @hasMany('template', { async: false, inverse: 'transactionalMessage' }) templates!: TemplateModel[];

  @attr('string') preview!: string;

  @attr('number') created!: number;
  @attr('number') updated!: number;

  @equal('contentSource', ContentSource.Api) isContentSourceApi!: boolean;
  @equal('contentSource', ContentSource.Template) isContentSourceTemplate!: boolean;
  @equal('contentSource', ContentSource.Default) isContentSourceDefault!: boolean;

  @equal('state', TransactionalMessageState.Draft) isDraft!: boolean;
  @equal('state', TransactionalMessageState.Active) isActive!: boolean;

  @not('isDefault') isNotDefault!: boolean;

  @hasMany('tag', { async: true, inverse: 'transactionalMessages' }) tags!: DS.PromiseManyArray<TagModel>;

  // type = 'push' - has no significance in the backend, but is used to determine which channel is selected in the UI
  @attr('string', { defaultValue: Platform.All }) platform!: Platform;
  @alias('platform') sendToPlatform!: Platform;
  @equal('sendToPlatform', Platform.All) isPlatformDefault!: boolean;
  @not('isPlatformDefault') isPlatformNotDefault!: boolean;
  @and('sendToPlatform', 'isPlatformNotDefault') isPlatformSelected!: boolean;

  tagSorting = ['name'];
  @sort('tags.content', 'tagSorting') sortedTags!: TagModel[];

  get hasContentSource() {
    return this.contentSource === ContentSource.Api || this.contentSource === ContentSource.Template;
  }

  get hasAllSettings() {
    return SettingsKeys.every((key) => this[key] !== null);
  }

  @computed('template.{body,bodyJson,templateType}', 'type')
  get hasTemplateContent() {
    let isIdPresent = Boolean(this.belongsTo.call(this, 'template').id());
    if (!isIdPresent) {
      return false;
    }

    let isContentPresent = this.template.get('templateType') === this.type;

    switch (this.type) {
      case TransactionalMessageChannelType.Email:
        isContentPresent &&= !isEmpty(this.template.get('body'));
        break;
      case TransactionalMessageChannelType.Push:
        // body json is non-empty for both rich and custom payload
        isContentPresent &&= !isEmpty(this.template.get('bodyJson'));
        break;
    }

    return isIdPresent && isContentPresent;
  }

  get hasSentMessage() {
    return false;
  }

  get settingsDescription() {
    let description: string[] = [];

    description.push(
      `Messages ${this.sendToUnsubscribed ? 'will' : 'will not'} send to unsubscribed people${
        this.queueDrafts ? ' and queue as drafts' : ''
      }`,
    );

    if (this.type === TransactionalMessageChannelType.Email) {
      description.push(`link clicks ${this.linkTracking ? 'will' : 'will not'} be tracked`);
    }

    if (this.hideMessageBody) {
      description.push('message body will be hidden to protect sensitive data');
    }

    return joinWords(description);
  }

  @computed('isDefault', 'name')
  get displayName() {
    return this.isDefault ? 'All Uncategorized Messages' : this.name;
  }

  @computed('preview')
  get sanitizedPreview() {
    return sanitize(this.preview ?? '', config);
  }

  addTags = modelApiAction({
    path: 'tags',
    type: 'POST',
    before(tags: TagModel[]) {
      return {
        tags: tags.map((tag) => ({ id: Number(tag.id), name: tag.name })),
      };
    },

    after(this: TransactionalMessageModel, response) {
      this.store.pushPayload(response);
      return response;
    },
  });

  copy = modelApiAction({
    path: 'copy',
    type: 'POST',
    before(data: { envID?: string }) {
      return {
        copy_to_env: data.envID ? Number(data.envID) : 0,
      };
    },
  });

  async removeTag(tag: TagModel) {
    await this.api.delete(`transactional_messages/${this.id}/tags/${tag.id}`, {});

    this.set(
      'tags',
      (this.tags.content as TagModel[]).filter((t) => t !== tag),
    );
  }

  addTranslations = modelApiAction({
    path: 'translations',
    type: 'POST',

    before({ languages }: { languages: string[] }) {
      return { languages };
    },
    after(this: TransactionalMessageModel, response) {
      this.store.pushPayload(response);
      return response;
    },
  });
}

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'transactional-message': TransactionalMessageModel;
  }
}
